import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useApiMutation } from '../../apis/useApi';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import ClayIcon from '@clayui/icon';
import './PartnerOffersPage.css';
import Loader from './custom-loader/Loader';
import NoDataFound from '../../generic-components/NoDataFound/NoDataFound';
import { getFormattedAmount } from '../../utils/commanUtil';
import ReadMoreText from '../ReadMoreText/ReadMoreText';
import { LANGUAGE } from '../../utils/languageProperties';
interface Partner {
    id: string;
    name: string;
    logo: string;
}

const PartnerOffersPage = () => {
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement>(null);
    const { searchValue, setSearchValue } = useOutletContext<any>();

    const {
        taxonomyCategoryIds,
        taxonomySubCategoryIds,
        categoryId,
        partnerId
    } = useParams<{
        taxonomyCategoryIds: string;
        taxonomySubCategoryIds: string;
        categoryId: string;
        partnerId: string;
    }>();

    const [offers, setOffers] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);

    const [partners, setPartners] = useState<Partner[]>([]);
    const [activePartnerId, setActivePartnerId] = useState<string | undefined>(
        partnerId
    );

    const [partnerDescription, setPartnerDescription] = useState('');
    const [logo, setLogo] = useState('');

    const languageId =
        (window as any)?.Liferay?.ThemeDisplay?.getLanguageId?.() || 'en_US';
    const currentLocale =
        (window as any)?.Liferay?.ThemeDisplay?.getBCP47LanguageId?.() ||
        'en-US';
    const isArabicPortal = String(languageId).toLowerCase().startsWith('ar');

    const scopeKey = 'BID Partner';

    const getPartners = useApiMutation<any, {}>(
        `o/c/partneroffers/scopes/${scopeKey}`,
        'get'
    );

    const getPartnerOffers = useApiMutation<any, {}>(
        `o/c/partneroffers/scopes/${scopeKey}`,
        'get'
    );

    const isLoading = getPartnerOffers?.isLoading || getPartners?.isLoading;

    const selectedSubService = JSON.parse(
        sessionStorage.getItem('selectedSubServiceItem') || 'null'
    );

    const formatValue = (value: string | undefined | null) => {
        if (
            value === undefined ||
            value === null ||
            value === '' ||
            value === 'null'
        ) {
            return '-';
        }
        return value;
    };

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };

    const getLocalizedAmount = (rawValue: any) => {
        const formatted = getFormattedAmount(formatValue(rawValue));

        if (!formatted || formatted === '-') return formatted;

        return formatted.replace(/^AED\b/i, LANGUAGE['aed']);
    };

    const parseI18n = (value: any): Record<string, string> => {
        if (!value) return {};

        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch {
                return {};
            }
        }

        return typeof value === 'object' ? value : {};
    };

    const getLocaleValue = (
        value: any,
        localeKeys: string[],
        fallback = ''
    ) => {
        const parsed = parseI18n(value);

        for (const localeKey of localeKeys) {
            if (typeof parsed?.[localeKey] === 'string' && parsed[localeKey]) {
                return parsed[localeKey];
            }

            const matchedKey = Object.keys(parsed).find(
                (key) => key.toLowerCase() === localeKey.toLowerCase()
            );

            if (
                matchedKey &&
                typeof parsed?.[matchedKey] === 'string' &&
                parsed[matchedKey]
            ) {
                return parsed[matchedKey];
            }
        }

        return fallback;
    };

    const getLocalizedText = (i18nValue: any, fallback = '') => {
        const currentLanguage = String(languageId || 'en_US').replace('-', '_');

        return isArabicPortal
            ? getLocaleValue(
                  i18nValue,
                  [
                      currentLanguage,
                      'ar_SA',
                      'ar_AE',
                      'ar_AR',
                      'ar_Ar',
                      'ar_EG',
                      'ar'
                  ],
                  fallback
              )
            : getLocaleValue(
                  i18nValue,
                  [currentLanguage, 'en_US', 'en_EN', 'en_GB', 'en'],
                  fallback
              );
    };

    /* ---------------- GET PARTNERS ---------------- */

    useEffect(() => {
        if (!taxonomySubCategoryIds) return;

        getPartners.mutate(
            {
                data: {},
                params: {
                    p_auth: (window as any)?.Liferay?.authToken,
                    nestedFields: 'partnerToOffer',
                    fields: 'partnerToOffer.id,partnerToOffer.companyName,partnerToOffer.companyNameI18n,partnerToOffer.companyLogo',
                    filter: `(offerStatus ne 'suspendedServiceOffer' and offerStatus ne 'deactivatedServiceOffer' and offerStatus ne 'pendingOfferApprovalFromPartner' and offerStatus ne 'pendingOfferApprovalFromAccountManager' and offerStatus ne 'accountManagerNeedRevision' and offerStatus ne 'partnerNeedRevision') and taxonomyCategoryIds/any(t:t eq ${taxonomySubCategoryIds})`
                }
            },
            {
                onSuccess: (data) => {
                    const map = new Map();

                    data?.items?.forEach((item: any) => {
                        const partner = item.partnerToOffer;

                        if (partner && !map.has(partner.id)) {
                            map.set(partner.id, {
                                id: partner.id,
                                name: getLocalizedText(
                                    partner.companyNameI18n,
                                    partner.companyName || ''
                                ),
                                logo: partner.companyLogo?.link?.href
                            });
                        }
                    });

                    setPartners(Array.from(map.values()));
                }
            }
        );
    }, [taxonomySubCategoryIds, languageId]);

    /* ---------------- GET OFFERS ---------------- */

    const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1200);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!activePartnerId) return;

        getPartnerOffers.mutate(
            {
                data: {},
                params: {
                    p_auth: (window as any)?.Liferay?.authToken,
                    pageSize: 0,
                    nestedFields: 'partnerToOffer',
                    filter: `r_partnerToOffer_c_corporatePartnerId eq '${activePartnerId}' and (offerStatus ne 'suspendedServiceOffer' and offerStatus ne 'deactivatedServiceOffer' and offerStatus ne 'pendingOfferApprovalFromPartner' and offerStatus ne 'pendingOfferApprovalFromAccountManager' and offerStatus ne 'accountManagerNeedRevision' and offerStatus ne 'partnerNeedRevision') and taxonomyCategoryIds/any(t:t eq ${taxonomySubCategoryIds})`
                }
            },
            {
                onSuccess: (data) => {
                    const items = data?.items ?? [];

                    if (items.length === 0) {
                        setOffers([]);
                        return;
                    }

                    const partner = items[0]?.partnerToOffer;

                    setPartnerDescription(
                        getLocalizedText(
                            partner?.companyOverviewI18n,
                            partner?.companyOverview || ''
                        )
                    );
                    setLogo(partner?.companyLogo?.link?.href || '');

                    const mappedOffers = items.map((item: any) => ({
                        localizedServiceOfferName: getLocalizedText(
                            item.serviceOfferNameI18n,
                            item.serviceOfferName || ''
                        ),
                        localizedDescription: getLocalizedText(
                            item.descriptionI18n,
                            item.description || ''
                        ),
                        localizedBenefits: getLocalizedText(
                            item.benefitsI18n,
                            item.benefit || ''
                        ),
                        localizedEligibility: getLocalizedText(
                            item.primaryEligibilityCriteriaI18n,
                            item.primaryEligibilityCriteria || ''
                        ),
                        id: item.id,
                        title:
                            item.taxonomyCategoryBriefs?.[0]
                                ?.taxonomyCategoryName ||
                            item.serviceOfferName ||
                            'N/A',
                        serviceOfferName: item.serviceOfferName || '',
                        serviceOfferValue: item.serviceOfferValue || '',
                        valueAdded: item.valueAdded || '',
                        discount: item.discount || '',
                        volumeYear: item.volumeYear || '',
                        numberOfAddOnsExpectedToBeConsumed:
                            item.numberOfAddOnsExpectedToBeConsumed || '',
                        numberOfCustomersShouldGetIncentive:
                            item.numberOfCustomersShouldGetIncentive || '',
                        numberOfDiscountsExpectingToBeConsumed:
                            item.numberOfDiscountsExpectingToBeConsumed || '',
                        actualCustomers: item.actualCustomers || '',
                        description: item.description ?? '-',
                        benefits: item.benefit || '',
                        eligibility: item.primaryEligibilityCriteria || '',
                        startDate: item.startDate || '',
                        endDate: item.endDate || '',
                        serviceOfferType: item.serviceOfferType,
                        partnerStatus: partner?.partnerStatus,
                        volumeType: item?.volumeType,
                        customerPrice: item?.customerPrice
                    }));

                    setOffers(mappedOffers);
                    setFilteredData(mappedOffers);
                }
            }
        );
    }, [activePartnerId, languageId]);

    useEffect(() => {
        const normalizedSearch = searchValue?.trim().toLowerCase() || '';

        if (!normalizedSearch) {
            setFilteredData(offers);
            return;
        }

        const fields = [
            'localizedServiceOfferName',
            'localizedDescription',
            'localizedBenefits',
            'localizedEligibility',
            'serviceOfferName',
            'description',
            'benefits',
            'eligibility'
        ];

        const filtered = offers.filter((item) =>
            fields.some((field) =>
                item[field]?.toLowerCase().includes(normalizedSearch)
            )
        );

        setFilteredData(filtered);
    }, [searchValue, offers]);

    const isOfferDisabled = (offer: any) => {
        const today = new Date().setHours(0, 0, 0, 0);
        const startDate = new Date(offer?.startDate).setHours(0, 0, 0, 0);
        const endDate = new Date(offer?.endDate).setHours(0, 0, 0, 0);

        const isNotStarted = today < startDate;
        const isExpired = endDate < today;

        const isSuspended = [
            'pendingSuspensionApprovalFromSeniorManager',
            'pendingSuspensionApprovalFromVP',
            'pendingSuspensionApprovalFromPCEO'
        ].includes(offer?.partnerStatus?.key ?? '');

        const isLimitReached =
            offer?.volumeType?.key !== 'unlimited' &&
            Number(offer?.actualCustomers ?? 0) >=
                Number(offer?.volumeYear ?? 0);

        return isNotStarted || isExpired || isSuspended || isLimitReached;
    };

    const scrollLeft = () => {
        if (scrollRef.current) {
            const card = scrollRef.current.querySelector(
                '.offer-card'
            ) as HTMLElement;
            if (card) {
                const style = window.getComputedStyle(scrollRef.current);
                const gap =
                    parseInt(style.gap) || parseInt(style.columnGap) || 0;
                scrollRef.current.scrollBy({
                    left: -(card.offsetWidth + gap),
                    behavior: 'smooth'
                });
            } else {
                scrollRef.current.scrollBy({ left: -600, behavior: 'smooth' });
            }
        }
    };

    const scrollRight = () => {
        if (scrollRef.current) {
            const card = scrollRef.current.querySelector(
                '.offer-card'
            ) as HTMLElement;
            if (card) {
                const style = window.getComputedStyle(scrollRef.current);
                const gap =
                    parseInt(style.gap) || parseInt(style.columnGap) || 0;
                scrollRef.current.scrollBy({
                    left: card.offsetWidth + gap,
                    behavior: 'smooth'
                });
            } else {
                scrollRef.current.scrollBy({ left: 600, behavior: 'smooth' });
            }
        }
    };

    const handleOfferClick = (offer: any) => {
        setSearchValue('');

        sessionStorage.setItem(
            'selectedOffer',
            JSON.stringify({
                title:
                    offer?.localizedServiceOfferName || offer?.serviceOfferName,
                id: offer?.id,
                locale: currentLocale
            })
        );

        navigate(
            `/categories/${categoryId}/${taxonomyCategoryIds}/${taxonomySubCategoryIds}/${partnerId}/login/${offer?.id}`
        );

        window.scrollTo(0, 0);
    };

    return (
        <div className="offers-page">
            <div className="offers-container">
                <Breadcrumbs setSearchValue={setSearchValue} />

                <div className="offers-content-wrapper">
                    <div className="partner-header">
                        <h2 className="page-title">
                            {selectedSubService?.title}
                        </h2>

                        {/* ---------------- PARTNER SELECTOR ---------------- */}

                        <div className="partner-selector">
                            {partners.map((p) => (
                                <button
                                    key={p.id}
                                    className={`partner-tab ${
                                        activePartnerId == p.id ? 'active' : ''
                                    }`}
                                    onClick={() => setActivePartnerId(p.id)}
                                >
                                    {p.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            {/* ---------------- PARTNER INFO ---------------- */}

                            <div className="partner-info">
                                <img
                                    src={logo}
                                    className="partner-offer-logo"
                                    alt="logo"
                                />
                                <ReadMoreText
                                    text={partnerDescription}
                                    maxLength={200}
                                />
                            </div>

                            {/* ---------------- OFFERS ---------------- */}
                            <div className="offers-wrapper">
                                {filteredData.length > (isMobile ? 1 : 3) && (
                                    <button
                                        className="scroll-btn left"
                                        onClick={scrollLeft}
                                    >
                                        ◀
                                    </button>
                                )}
                                {Boolean(filteredData?.length) ? (
                                    <>
                                        <div
                                            className="offers-slider"
                                            ref={scrollRef}
                                        >
                                            {filteredData.map((offer: any) => (
                                                <div
                                                    key={offer.id}
                                                    className={`offer-card ${
                                                        filteredData?.length ===
                                                        1
                                                            ? 'single'
                                                            : filteredData?.length ===
                                                                2
                                                              ? 'double'
                                                              : 'multiple'
                                                    }`}
                                                >
                                                    <h2 className="service-detail-title">
                                                        {formatValue(
                                                            offer?.localizedServiceOfferName ||
                                                                offer?.serviceOfferName
                                                        )}
                                                    </h2>

                                                    <p className="service-detail-field">
                                                        {
                                                            LANGUAGE[
                                                                'service-offer-value'
                                                            ]
                                                        }
                                                        :{' '}
                                                        <span>
                                                            {getLocalizedAmount(
                                                                offer?.serviceOfferValue
                                                            )}
                                                        </span>
                                                    </p>
                                                    {![
                                                        'addOn',
                                                        'incentive'
                                                    ]?.includes(
                                                        offer?.serviceOfferType
                                                            ?.key
                                                    ) && (
                                                        <>
                                                            <p className="service-detail-field">
                                                                {
                                                                    LANGUAGE[
                                                                        'discount'
                                                                    ]
                                                                }
                                                                :{' '}
                                                                <span>
                                                                    {getLocalizedAmount(
                                                                        offer?.discount
                                                                    )}
                                                                </span>
                                                            </p>
                                                        </>
                                                    )}
                                                    {offer?.valueAdded &&
                                                        ['addOn']?.includes(
                                                            offer
                                                                ?.serviceOfferType
                                                                ?.key
                                                        ) &&
                                                        parseFloat(
                                                            offer.valueAdded
                                                                .toString()
                                                                .replace(
                                                                    /[^\d.]/g,
                                                                    ''
                                                                ) || '0'
                                                        ) > 1 && (
                                                            <p className="service-detail-field">
                                                                {
                                                                    LANGUAGE[
                                                                        'value-added-add-on'
                                                                    ]
                                                                }
                                                                :{' '}
                                                                <span>
                                                                    {getLocalizedAmount(
                                                                        offer?.valueAdded
                                                                    )}
                                                                </span>
                                                            </p>
                                                        )}
                                                    {offer?.valueAdded &&
                                                        [
                                                            'discountWithAddOn'
                                                        ]?.includes(
                                                            offer
                                                                ?.serviceOfferType
                                                                ?.key
                                                        ) &&
                                                        parseFloat(
                                                            offer.valueAdded
                                                                .toString()
                                                                .replace(
                                                                    /[^\d.]/g,
                                                                    ''
                                                                ) || '0'
                                                        ) > 1 && (
                                                            <p className="service-detail-field">
                                                                {
                                                                    LANGUAGE[
                                                                        'value-added-discount-with-add-on'
                                                                    ]
                                                                }
                                                                :{' '}
                                                                <span>
                                                                    {getLocalizedAmount(
                                                                        offer?.valueAdded
                                                                    )}
                                                                </span>
                                                            </p>
                                                        )}

                                                    <p className="service-detail-field">
                                                        {
                                                            LANGUAGE[
                                                                'members-price'
                                                            ]
                                                        }
                                                        :{' '}
                                                        <span>
                                                            {getLocalizedAmount(
                                                                offer?.customerPrice
                                                            )}
                                                        </span>
                                                    </p>
                                                    <div className="service-detail-heading">
                                                        {
                                                            LANGUAGE[
                                                                'descriptions'
                                                            ]
                                                        }
                                                    </div>
                                                    <ul className="service-detail-list">
                                                        {offer?.localizedDescription &&
                                                        formatValue(
                                                            offer?.localizedDescription
                                                        ) !== '-' ? (
                                                            offer?.localizedDescription
                                                                .split('\n')
                                                                .filter(
                                                                    (d: any) =>
                                                                        d.trim() !==
                                                                        ''
                                                                )
                                                                .map(
                                                                    (
                                                                        d: any,
                                                                        i: number
                                                                    ) => (
                                                                        <li
                                                                            key={
                                                                                i
                                                                            }
                                                                            dir={
                                                                                isArabicPortal
                                                                                    ? 'rtl'
                                                                                    : 'ltr'
                                                                            }
                                                                            style={{
                                                                                textAlign:
                                                                                    isArabicPortal
                                                                                        ? 'right'
                                                                                        : 'left'
                                                                            }}
                                                                        >
                                                                            {formatValue(
                                                                                d
                                                                            )}
                                                                        </li>
                                                                    )
                                                                )
                                                        ) : (
                                                            <li>-</li>
                                                        )}
                                                    </ul>

                                                    <div className="service-detail-heading">
                                                        {LANGUAGE['benefits']}
                                                    </div>
                                                    <ul className="service-detail-list">
                                                        {offer?.localizedBenefits &&
                                                        formatValue(
                                                            offer?.localizedBenefits
                                                        ) !== '-' ? (
                                                            offer?.localizedBenefits
                                                                .split('\n')
                                                                .filter(
                                                                    (b: any) =>
                                                                        b.trim() !==
                                                                        ''
                                                                )
                                                                .map(
                                                                    (
                                                                        b: any,
                                                                        i: number
                                                                    ) => (
                                                                        <li
                                                                            key={
                                                                                i
                                                                            }
                                                                            dir={
                                                                                isArabicPortal
                                                                                    ? 'rtl'
                                                                                    : 'ltr'
                                                                            }
                                                                            style={{
                                                                                textAlign:
                                                                                    isArabicPortal
                                                                                        ? 'right'
                                                                                        : 'left'
                                                                            }}
                                                                        >
                                                                            {formatValue(
                                                                                b
                                                                            )}
                                                                        </li>
                                                                    )
                                                                )
                                                        ) : (
                                                            <li>-</li>
                                                        )}
                                                    </ul>

                                                    <div className="service-detail-heading">
                                                        {
                                                            LANGUAGE[
                                                                'eligibility-criteria'
                                                            ]
                                                        }
                                                    </div>
                                                    <ul className="service-detail-list">
                                                        {offer?.localizedEligibility &&
                                                        formatValue(
                                                            offer?.localizedEligibility
                                                        ) !== '-' ? (
                                                            offer?.localizedEligibility
                                                                .split('\n')
                                                                .filter(
                                                                    (e: any) =>
                                                                        e.trim() !==
                                                                        ''
                                                                )
                                                                .map(
                                                                    (
                                                                        e: any,
                                                                        i: any
                                                                    ) => (
                                                                        <li
                                                                            key={
                                                                                i
                                                                            }
                                                                            dir={
                                                                                isArabicPortal
                                                                                    ? 'rtl'
                                                                                    : 'ltr'
                                                                            }
                                                                            style={{
                                                                                textAlign:
                                                                                    isArabicPortal
                                                                                        ? 'right'
                                                                                        : 'left'
                                                                            }}
                                                                        >
                                                                            {formatValue(
                                                                                e
                                                                            )}
                                                                        </li>
                                                                    )
                                                                )
                                                        ) : (
                                                            <li>-</li>
                                                        )}
                                                    </ul>
                                                    <p
                                                        style={{
                                                            marginTop: '16px'
                                                        }}
                                                    >
                                                        <b className="service-detail-heading">
                                                            {
                                                                LANGUAGE[
                                                                    'start-date'
                                                                ]
                                                            }
                                                            :
                                                        </b>{' '}
                                                        <b className="info">
                                                            {formatDate(
                                                                offer?.startDate
                                                            )}
                                                        </b>
                                                    </p>
                                                    <button
                                                        className="apply-btn"
                                                        disabled={isOfferDisabled(
                                                            offer
                                                        )}
                                                        onClick={() =>
                                                            handleOfferClick(
                                                                offer
                                                            )
                                                        }
                                                    >
                                                        {LANGUAGE['apply-now']}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <NoDataFound />
                                )}
                                {filteredData.length > (isMobile ? 1 : 3) && (
                                    <button
                                        className="scroll-btn right"
                                        onClick={scrollRight}
                                    >
                                        ▶
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartnerOffersPage;
